/**
 * Postage in the composer (docs/POSTAGE_PLAN.md §3).
 *
 * - `usePostageChoice` — which stamp this letter goes on: the member's sticky
 *   class, unless they pick another for this one letter. Express is never a
 *   default; it is a stamp, spent on purpose.
 * - The stamp on the compose card and the stamp book it opens live in
 *   `StampPicker.tsx`.
 * - `PostageOfferModal` — shown once, the first time a member opens a
 *   composer after the pair earns a higher class.
 * - `LetterStamp` — the stamp on an envelope in the thread.
 *
 * Everything renders nothing while postage is switched off, so the composer
 * and the envelopes look exactly as they did before.
 */

import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { POSTAGE_LABELS, type PostageClass } from '@toiyp/shared';
import {
  usePostageQuery,
  useSetPostagePreferenceMutation,
  type ApiLetter,
  type PostageView,
} from '../letters/api';
import { useDialogTrap } from '../letters/useDialogTrap';
import { useAppSettings } from '../settings/api';
import { FrankedStamp, PostageStamp } from './Stamp';
import { etaLong } from './format';
import { postageNotice } from './notice';
import { takeReadiedClass } from './intent';
import { useStampDesign, type DesignChoice } from './designs';
import { STAMP_BOOK_HASH } from './StampBook';

type ClassView = PostageView['classes'][number];

/* ── The choice ─────────────────────────────────────────────────────────── */

export interface PostageChoice extends DesignChoice {
  relationshipId: string;
  view: PostageView | undefined;
  enabled: boolean;
  /** The class this letter will be sealed with, or undefined when postage is off. */
  selected: PostageClass | undefined;
  selectedView: ClassView | undefined;
  /**
   * Pick a class. 1st/2nd also become the sticky default (unless `persist` is
   * false, for a caller saving it itself); Express stays per letter.
   */
  choose: (cls: PostageClass, persist?: boolean) => void;
}

export function usePostageChoice(relationshipId: string, opts: { skip?: boolean } = {}): PostageChoice {
  const { data: view } = usePostageQuery(
    { relationshipId },
    { refetchOnMountOrArgChange: 30, skip: opts.skip },
  );
  const [savePreference] = useSetPostagePreferenceMutation();
  const designChoice = useStampDesign({ skip: opts.skip, relationshipId });
  const [chosen, setChosen] = useState<PostageClass | null>(null);
  const enabled = Boolean(view?.enabled);

  // A stamp tapped in the stamp book readies its class for this composer.
  useEffect(() => {
    if (!enabled || opts.skip) return;
    const readied = takeReadiedClass(relationshipId);
    if (readied) setChosen(readied);
  }, [enabled, opts.skip, relationshipId]);

  const usable = useMemo(
    () => new Set((view?.classes ?? []).filter((c) => c.state === 'available').map((c) => c.class)),
    [view],
  );
  const selected: PostageClass | undefined = !enabled
    ? undefined
    : chosen && usable.has(chosen)
      ? chosen
      : view!.preferred;
  const selectedView = view?.classes.find((c) => c.class === selected);

  const choose = useCallback(
    (cls: PostageClass, persist = true) => {
      setChosen(cls);
      if (persist && cls !== 'express') void savePreference({ relationshipId, preferred: cls });
    },
    [relationshipId, savePreference],
  );

  return { relationshipId, view, enabled, selected, selectedView, choose, ...designChoice };
}

/** The seal button's words, naming Express when a stamp is being spent. */
export function sealLabel(base: string, choice: PostageChoice): string {
  return choice.selected === 'express' ? `${base} · Express` : base;
}

/* ── The once-only offer ───────────────────────────────────────────────── */

/**
 * Shown the first time the composer opens after the pair earns a higher class.
 * It announces rather than asks: an earned pair is already on 1st class, and
 * the one choice offered is to keep 2nd class instead. Never a nag.
 */
export function PostageOfferModal({ choice, peerName }: { choice: PostageChoice; peerName: string }) {
  const [savePreference] = useSetPostagePreferenceMutation();
  const [closed, setClosed] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { relationshipId } = choice;
  const offer = choice.view?.offer ?? null;

  // The offer is an announcement: 1st class is already on (the server defaults
  // an earned pair to it). Dismissing in any way marks it seen; only "Keep 2nd
  // class instead" saves a choice.
  const finish = useCallback(
    (keepSecond: boolean) => {
      setClosed(true);
      if (keepSecond) choice.choose('second', false);
      void savePreference({ relationshipId, seen: true, ...(keepSecond ? { preferred: 'second' as const } : {}) });
    },
    [choice, relationshipId, savePreference],
  );
  const dismiss = useCallback(() => finish(false), [finish]);

  if (!offer || closed || !choice.enabled) return null;
  return (
    <OfferDialog
      offer={offer}
      choice={choice}
      peerName={peerName}
      onKeepSecond={() => finish(true)}
      onDismiss={dismiss}
      dialogRef={ref}
    />
  );
}

function OfferDialog({
  offer,
  choice,
  peerName,
  onKeepSecond,
  onDismiss,
  dialogRef,
}: {
  offer: NonNullable<PostageView['offer']>;
  choice: PostageChoice;
  peerName: string;
  onKeepSecond: () => void;
  onDismiss: () => void;
  dialogRef: React.RefObject<HTMLDivElement | null>;
}) {
  useDialogTrap(dialogRef, onDismiss);
  const byClass = (cls: PostageClass) => choice.view?.classes.find((c) => c.class === cls);
  const second = byClass('second');
  const first = byClass('first');
  const offersFirst = offer.class === 'first' || offer.includesFirst;
  const name = `@${peerName}`;
  const n = offer.expressBalance;

  return createPortal(
    <div className="postage-overlay" onClick={onDismiss}>
      <div
        className="postage-modal postage-offer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="postage-offer-title"
        ref={dialogRef}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <PostageStamp cls={offer.class} size="large" postmark={new Date()} />
        <h2 id="postage-offer-title" className="postage-modal__title">
          {offer.class === 'express' ? `You've earned Express with ${name}` : `1st class is now on with ${name}`}
        </h2>
        {offer.reasons.length > 0 && (
          <ul className="postage-offer__why">
            {offer.reasons.slice(0, 2).map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
        )}
        {offersFirst && (
          <p className="postage-modal__sub">Your letters to {name} go 1st class from now on.</p>
        )}
        {offersFirst && second && first && (
          <ol className="postage-offer__compare" aria-label="Arrival if sealed now">
            <li>
              <PostageStamp cls="second" size="mini" />
              <span>2nd class · {etaLong(second.deliverAt)}</span>
            </li>
            <li className="postage-offer__compare-to">
              <PostageStamp cls="first" size="mini" />
              <span>1st class · {etaLong(first.deliverAt)}</span>
            </li>
          </ol>
        )}
        {offer.class === 'express' && (
          <p className="postage-modal__sub">
            {n > 0
              ? `You have ${n} Express stamp${n === 1 ? '' : 's'} for ${name}. Save them for letters that can't wait.`
              : `Express stamps for ${name} arrive each month. Save them for letters that can't wait.`}
          </p>
        )}
        <button type="button" className="btn btn--primary postage-modal__cta" onClick={onDismiss}>
          Got it
        </button>
        {offersFirst && (
          <button type="button" className="linkish postage-offer__secondary" onClick={onKeepSecond}>
            Keep 2nd class instead
          </button>
        )}
        <p className="postage-modal__note">You can change this on any letter. 2nd class is always free.</p>
      </div>
    </div>,
    document.body,
  );
}

/* ── After the seal ────────────────────────────────────────────────────── */

/** The one-line notice host — mount once, near the app root. */
export function PostageNoticeHost() {
  const notice = useSyncExternalStore(postageNotice.subscribe, postageNotice.get, postageNotice.get);
  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => postageNotice.clear(notice.id), 6000);
    return () => clearTimeout(t);
  }, [notice]);
  return (
    <div className="postage-notice-host" aria-live="polite">
      {notice && (
        <p className="postage-notice" role="status">
          <PostageStamp cls="second" size="mini" />
          <span>{notice.text}</span>
          <button type="button" className="postage-notice__close" onClick={() => postageNotice.clear()} aria-label="Dismiss">
            <X size={13} />
          </button>
        </p>
      )}
    </div>
  );
}

/* ── On the envelope ───────────────────────────────────────────────────── */

/**
 * The stamp a letter travelled on, in the envelope's top-right corner, franked
 * with a postmark once the van has collected it (Express: at sealing). Every
 * letter shows one once postage is on; while it is off, only a letter that
 * actually went 1st class or Express does, so the thread looks as it always has.
 */
export function LetterStamp({ letter, size = 'medium' }: { letter: ApiLetter; size?: 'small' | 'medium' }) {
  const { postageEnabled } = useAppSettings();
  const cls = letter.postageClass ?? 'second';
  const design = letter.stampDesign ?? null;
  if (!postageEnabled && cls === 'second' && !design) return null;
  const franked = frankedAt(letter);
  // A design is someone's artwork: it goes on a size up, so it reads.
  const shown = design ? (size === 'small' ? 'medium' : 'sheet') : size;
  return (
    <span
      className={`letter-stamp${design ? ' letter-stamp--design' : ''}`}
      style={{ '--stamp-tilt': `${stampTilt(letter.id)}deg` } as React.CSSProperties}
      title={`Sent ${POSTAGE_LABELS[cls]}${design ? ` · ${design.name}` : ''}`}
    >
      <FrankedStamp cls={cls} date={franked} size={shown} design={design} />
      <span className="visually-hidden">Sent {POSTAGE_LABELS[cls]}</span>
    </span>
  );
}

/**
 * The angle a stamp was stuck on at, in the chat history: a little crooked,
 * never level, and the same every time the letter is drawn (seeded by its id).
 */
export function stampTilt(id: string): number {
  let h = 2166136261;
  for (let i = 0; i < id.length; i++) h = Math.imul(h ^ id.charCodeAt(i), 16777619) >>> 0;
  const mag = 2 + (h % 26) / 10; // 2.0°–4.5°
  return (h >>> 16) & 1 ? -mag : mag;
}

/** When the van franked it — null while it still waits in the postbox. */
function frankedAt(letter: ApiLetter): Date | null {
  const collected = letter.collectedAt ? new Date(letter.collectedAt) : null;
  return collected && collected.getTime() <= Date.now() ? collected : null;
}

/**
 * The stamp in the full letter's top-right corner, like a real envelope —
 * every letter has one, a design or the plain class stamp, franked with its
 * postmark once collected. A tap names it (and the book a design is from).
 */
export function LetterPageStamp({ letter }: { letter: ApiLetter }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const cls = letter.postageClass ?? 'second';
  const design = letter.stampDesign ?? null;
  const franked = frankedAt(letter);
  const label = POSTAGE_LABELS[cls];

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="letter-page-stamp" ref={ref}>
      <button
        type="button"
        className="letter-page-stamp__btn"
        aria-expanded={open}
        aria-label={`Stamp: ${label}${design ? `, ${design.name}` : ''}. About this stamp`}
        onClick={() => setOpen((o) => !o)}
      >
        <FrankedStamp cls={cls} date={franked} size="envelope" design={design} />
      </button>
      {open && (
        <div className="letter-page-stamp__card" role="status">
          <strong>{design ? design.name : `${label} stamp`}</strong>
          {design?.collectionName && <span>from the {design.collectionName} stamp book</span>}
          <span>
            {label} ·{' '}
            {franked
              ? `franked ${franked.toLocaleDateString([], { day: 'numeric', month: 'long' })}`
              : 'still in the postbox'}
          </span>
          {design && (
            <button
              type="button"
              className="linkish"
              onClick={() => {
                setOpen(false);
                window.location.hash = STAMP_BOOK_HASH;
              }}
            >
              See stamp books
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/** Whether envelopes and the route carry a class stamp at all. */
export function useShowsStamp(cls: PostageClass | null | undefined): boolean {
  const { postageEnabled } = useAppSettings();
  return cls != null && (postageEnabled || cls !== 'second');
}
